import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { environment } from '@lohi/environments/environment';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { filter, map, share, shareReplay, switchMap, tap } from 'rxjs/operators';

export interface LAMUserInfo {
  id: string;
  driverName: string;
  phoneNumber: string;
  verified: boolean;
  verifiedTimestamp: string;
  companyName: string;
  companyId: string;
  companyOwnerName: string;
  companyOwnerPhone: string;
  permissions: string[];
  homebaseLng: number;
  homebaseLat: number;
  allowDriverBooking: boolean;
  allowDriverCreateTruck: boolean;
  allowDriverCreateTrailer: boolean;
  showOnlyTrailerTickets: boolean;
  isInternalUser?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private userInfo$$ = new BehaviorSubject<LAMUserInfo>(null);
  public userInfo$ = this.userInfo$$.asObservable().pipe(shareReplay(1));
  private httpClientNoInterceptors: HttpClient;
  private isLoggedIn$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  public isLoggedIn$: Observable<boolean> = this.isLoggedIn$$.asObservable().pipe(
    filter((v) => v !== null),
    shareReplay(1),
  );

  public isLoggedInWithNull$: Observable<boolean> = this.isLoggedIn$$.asObservable().pipe(
    filter((v) => v !== null),
    shareReplay(1),
  ) as Observable<boolean>;

  private showTrailerTicketsOnly$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  public showTrailerTicketsOnly$ = this.showTrailerTicketsOnly$$.asObservable().pipe(share());

  public isSafetyManager$(): Observable<boolean> {
    return this.userInfo$$.asObservable().pipe(
      filter((value) => value !== null),
      map((info) => info.permissions.indexOf('safety-manager') >= 0),
    );
  }

  public showOnlyTrailers$(): Observable<boolean> {
    return this.userInfo$$.asObservable().pipe(
      filter((value) => value !== null),
      map((info) => info.showOnlyTrailerTickets),
    );
  }

  constructor(httpBackend: HttpBackend, private afAuth: AngularFireAuth) {
    this.httpClientNoInterceptors = new HttpClient(httpBackend);

    this.getUserInfo();
  }

  public getUserInfo() {
    this.afAuth.authState.subscribe(async (user) => {
      if (user) {
        this.afAuth.idToken.subscribe((token) => {
          this.loadUserInfo$(token).subscribe();
        });
        this.isLoggedIn$$.next(true);
      } else {
        this.isLoggedIn$$.next(false);
        this.showTrailerTicketsOnly$$.next(false);
      }
    });
  }

  public loginWithCustomToken$(token: string): Observable<LAMUserInfo> {
    return from(this.afAuth.signInWithCustomToken(token)).pipe(
      switchMap((_) => {
        return this.afAuth.idToken;
      }),
      switchMap((userToken: string) => {
        return this.loadUserInfo$(userToken);
      }),
    );
  }

  public async logout() {
    await this.afAuth.signOut();
    window.location.pathname = '/';
  }

  public loadUserInfo$(token): Observable<LAMUserInfo> {
    return this.httpClientNoInterceptors
      .get<LAMUserInfo>(`${environment.api}/user/info`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      .pipe(
        tap((info) => {
          this.userInfo$$.next(info);
          this.isLoggedIn$$.next(true);
          this.showTrailerTicketsOnly$$.next(info.showOnlyTrailerTickets);
        }),
      );
  }

  public loginWithPhone$(phoneNumber: string, email: string, password: string): Observable<any> {
    return this.httpClientNoInterceptors
      .post<{ customToken: string }>(
        `${environment.api}/login`,
        { phoneNumber, email, password },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      .pipe(
        switchMap((result) => {
          return this.afAuth.signInWithCustomToken(result.customToken);
        }),
        switchMap((_) => {
          return this.afAuth.idToken;
        }),
        switchMap((userToken: string) => {
          return this.loadUserInfo$(userToken);
        }),
      );
  }
}
